
/**
 * Highlights keywords within a text string and truncates around the matches.
 * 
 * @param text The full text to process.
 * @param keywords Array of keywords to highlight.
 * @param maxLength Max length of the resulting snippet (default 150).
 * @returns ReactNode with highlighted matches.
 */
export function HighlightText({ text, keywords, maxLength = 150 }: { text?: string, keywords?: string[], maxLength?: number }) {
  if (!text) return null;
  
  // Clean text from HTML tags first (if any)
  const cleanText = text.replace(/<[^>]*>?/gm, '');
  
  if (!keywords || keywords.length === 0) {
      return (
          <span>
             {cleanText.length > maxLength ? `${cleanText.substring(0, maxLength)}...` : cleanText}
          </span>
      );
  }

  // Flatten keywords (split by space if user entered "marketing strategy" as one string)
  const searchTerms = keywords
    .flatMap(k => k.split(/\s+/))
    .filter(k => k.length > 2) // Ignore very short words
    .map(k => k.toLowerCase());

  if (searchTerms.length === 0) {
      return <span>{cleanText.length > maxLength ? `${cleanText.substring(0, maxLength)}...` : cleanText}</span>;
  }

  // Find first match index to determine where to start text (smart truncation)
  let firstMatchIndex = -1;
  const lowerText = cleanText.toLowerCase();

  for (const term of searchTerms) {
    const idx = lowerText.indexOf(term);
    if (idx !== -1) {
        if (firstMatchIndex === -1 || idx < firstMatchIndex) {
            firstMatchIndex = idx;
        }
    }
  }

  let displayText = cleanText;
  let prefix = "";

  // If match is found deep in the text AND text is longer than maxLength, truncate the beginning
  // This ensures that if we show the "Full" text (maxLength is large), we start from the beginning.
  if (cleanText.length > maxLength && firstMatchIndex > 50) {
      const start = Math.max(0, firstMatchIndex - 30); // Start 30 chars before match
      displayText = cleanText.substring(start);
      prefix = "...";
  }

  // Truncate the end
  if (displayText.length > maxLength) {
      displayText = displayText.substring(0, maxLength) + "...";
  }

  // Create Regex for highlighting
  // Escape special regex chars in keywords
  const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${searchTerms.map(escapeRegExp).join('|')})`, 'gi');

  const parts = (prefix + displayText).split(regex);

  return (
    <span>
      {parts.map((part, i) => 
        searchTerms.includes(part.toLowerCase()) ? (
          <span key={i} className="bg-yellow-200/60 font-medium text-slate-800 rounded-[2px] px-0.5 box-decoration-clone">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}
